import { useState, useEffect, useRef, useCallback } from 'react';
import { useI18n } from '@react/hooks';
import { Badge, Button } from '@react/components';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import { resourceApi } from '@core/api';
import { nextRelationType, RELATION_TYPE_CONFIG , urnToEntity } from '@/features/resources/logic';
import { searchResources, getExternalResourceByURL } from '@/features/resources/api';
import { errMessage } from '@/features/resources/format';
import type { Resource } from '@core/types/resource';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

interface ResourceCommandMenuProps {
  isOpen: boolean;
  onClose: () => void;
  entityUrn: string;
  entityType: string;
  defaultRelationType?: string;
  onLinked: (resource: Resource) => void;
}

export function ResourceCommandMenu({
  isOpen,
  onClose,
  entityUrn,
  entityType: _entityType,
  defaultRelationType,
  onLinked,
}: ResourceCommandMenuProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [selectedRelationType, setSelectedRelationType] = useState(
    defaultRelationType || 'RELATION_TYPE_REFERENCES',
  );
  const [searchResults, setSearchResults] = useState<Resource[]>([]);
  const [recentResources, setRecentResources] = useState<Resource[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<number>();

  // "Add URL" — register an external URL as a resource and link it here.
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [urlValue, setUrlValue] = useState('');
  const [isAddingUrl, setIsAddingUrl] = useState(false);
  const [addUrlError, setAddUrlError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      // Load recent resources
      resourceStore.ListRecentResources()
        .then(() => setRecentResources(resourceStore.items.slice(0, 10)))
        .catch(() => {});
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [isOpen]);

  useEffect(() => {
    if (defaultRelationType) {
      setSelectedRelationType(defaultRelationType);
    }
  }, [defaultRelationType]);

  const doSearch = useCallback((q: string) => {
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    searchResources(q)
      .then((res) => setSearchResults(res.resources?.items || []))
      .catch(() => setSearchResults([]))
      .finally(() => setIsSearching(false));
  }, []);

  const handleAddUrl = async () => {
    const url = urlValue.trim();
    if (!url) return;
    setIsAddingUrl(true);
    setAddUrlError(null);
    try {
      let externalEntity = (await getExternalResourceByURL(url).catch(() => null))?.resource?.entity;
      if (!externalEntity) {
        const created = await resourceApi.CreateExternalResource({ url });
        externalEntity = created.resource?.entity;
      }
      if (!externalEntity) throw new Error('No resource returned');
      const linkRes = await resourceStore.LinkResources({
        source: urnToEntity(entityUrn) as any,
        target: externalEntity as any,
        relation_type: selectedRelationType as any,
      });
      if (linkRes.link) {
        onLinked({
          entity: externalEntity,
          urn: linkRes.link.target_urn || '',
          title: url,
          subtitle: '',
          icon: '',
          color: '',
          relation_type: selectedRelationType,
        } as Resource);
      }
      setUrlValue('');
      setAddUrlOpen(false);
      onClose();
    } catch (e) {
      setAddUrlError(errMessage(e, t('rail.resources.addUrlError')));
    } finally {
      setIsAddingUrl(false);
    }
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => doSearch(value), 300);
  };

  const handleSelect = async (resource: Resource) => {
    try {
      await resourceStore.LinkResources({
        source: urnToEntity(entityUrn) as any,
        target: urnToEntity(resource.urn) as any,
        relation_type: selectedRelationType as any,
      });
      onLinked(resource);
      onClose();
    } catch {
      // error handled by store
    }
  };

  // Escape closes; Tab cycles the relation type (window-level, palette style).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        setSelectedRelationType((cur) => nextRelationType(cur));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const relationLabel = RELATION_TYPE_CONFIG[selectedRelationType]?.labelKey;
  const displayItems = query.trim() ? searchResults : recentResources;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
      <div
        className="relative w-full max-w-lg mx-4 bg-rail border border-rail-border rounded-lg shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label={t('rail.resources.searchResources')}
      >
        {/* Search header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-rail-border">
          <SearchIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            placeholder={t('rail.resources.searchResources')}
            className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
          />
          {relationLabel && (
            <Badge
              variant="secondary"
              size="sm"
              className="cursor-pointer flex-shrink-0"
              onClick={() => setSelectedRelationType(nextRelationType(selectedRelationType))}
            >
              {t(relationLabel)}
            </Badge>
          )}
          <kbd className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded flex-shrink-0">TAB</kbd>
        </div>

        {/* Content */}
        <div className="max-h-80 overflow-y-auto p-2">
          {/* Create new section */}
          <div className="px-2 py-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {t('rail.resources.createNew')}
            </p>
          </div>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left">
            <span className="text-primary">+</span>
            <span>{t('layout.quickCreate.task')}</span>
          </button>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left">
            <span className="text-primary">+</span>
            <span>{t('layout.quickCreate.document')}</span>
          </button>
          <button className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left">
            <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <span>{t('rail.resources.uploadFile')}</span>
          </button>
          <button
            type="button"
            onClick={() => setAddUrlOpen((o) => !o)}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
            aria-expanded={addUrlOpen}
          >
            <svg className="h-4 w-4 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
            </svg>
            <span>{t('rail.resources.addUrl')}</span>
          </button>
          {addUrlOpen && (
            <div className="px-3 py-2 space-y-2">
              <input
                type="url"
                value={urlValue}
                onChange={(e) => setUrlValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddUrl(); } }}
                placeholder="https://…"
                className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              {addUrlError && <p className="text-xs text-destructive">{addUrlError}</p>}
              <Button size="sm" onClick={handleAddUrl} isLoading={isAddingUrl} disabled={!urlValue.trim()}>
                {t('rail.resources.addUrl')}
              </Button>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-rail-border my-2" />

          {/* Results / Recent */}
          <div className="px-2 py-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
              {query.trim() ? t('layout.search.title') : t('rail.resources.linkExisting')}
            </p>
          </div>

          {isSearching && (
            <div className="flex items-center justify-center py-4">
              <div className="h-4 w-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {!isSearching && displayItems.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {query.trim() ? t('layout.search.noResults') : t('layout.rail.resourcesEmpty')}
            </p>
          )}

          {!isSearching && displayItems.map((resource) => (
            <button
              key={resource.entity?.id || resource.urn}
              onClick={() => handleSelect(resource)}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-foreground hover:bg-accent transition-colors text-left"
            >
              {resource.icon ? (
                <svg className="h-4 w-4 flex-shrink-0" style={{ color: resource.color || undefined }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d={resource.icon} />
                </svg>
              ) : (
                <div className="h-4 w-4 flex-shrink-0 rounded-sm bg-muted" />
              )}
              <div className="flex-1 min-w-0">
                <p className="truncate">{resource.title || resource.urn}</p>
                {resource.subtitle && (
                  <p className="text-xs text-muted-foreground truncate">{resource.subtitle}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
